import { useState } from 'react';
import { changeOrderStatus } from './api/orderStatusApi';

export interface OrderStatusFormValues {
  status: string;
  notes?: string;
}

interface WZConfirmationState {
  visible: boolean;
  message: string;
  values: OrderStatusFormValues | null;
}

interface UseOrderStatusChangeWithConfirmationOptions {
  orderId?: string | null;
  onSuccess: () => void | Promise<void>;
  onError: (error: unknown) => void;
}

const initialConfirmationState: WZConfirmationState = {
  visible: false,
  message: '',
  values: null,
};

export function useOrderStatusChangeWithConfirmation({
  orderId,
  onSuccess,
  onError,
}: UseOrderStatusChangeWithConfirmationOptions) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<WZConfirmationState>(initialConfirmationState);

  const submitStatusChange = async (values: OrderStatusFormValues, forceWithoutWZ = false) => {
    if (!orderId) return;

    setSubmitting(true);
    try {
      await changeOrderStatus(orderId, {
        ...values,
        forceWithoutWZ,
      });
      setConfirmation(initialConfirmationState);
      await onSuccess();
    } catch (error: any) {
      if (error.response?.data?.requiresConfirmation) {
        setConfirmation({
          visible: true,
          message: error.response.data.warning,
          values,
        });
      } else {
        onError(error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!confirmation.values) {
      setConfirmation(initialConfirmationState);
      return;
    }

    const values = confirmation.values;
    setConfirmation((current) => ({
      ...current,
      visible: false,
    }));
    await submitStatusChange(values, true);
  };

  const cancelConfirmation = () => {
    setConfirmation(initialConfirmationState);
  };

  return {
    confirmation,
    submitting,
    submitStatusChange,
    confirmStatusChange,
    cancelConfirmation,
  };
}
